import { useState, useEffect, useRef } from 'react';
import useNearViewport from './useNearViewport';

// IndexedDB cache implementation
const DB_NAME = 'ImageCache';
const DB_VERSION = 1;
const STORE_NAME = 'images';
const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

class ImageCacheDB {
    constructor() {
        this.db = null;
    }

    async init() {
        if (this.db) return this.db;

        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                this.db = request.result;
                resolve(this.db);
            };

            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'url' });
                    objectStore.createIndex('timestamp', 'timestamp', { unique: false });
                }
            };
        });
    }

    async get(url) {
        await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.get(url);

            request.onsuccess = () => {
                const result = request.result;
                if (result) {
                    // Check if cache is expired
                    const now = Date.now();
                    if (now - result.timestamp > CACHE_DURATION) {
                        // Cache expired, delete it (and the object URL built from it)
                        this.delete(url);
                        evictObjectUrl(url);
                        resolve(null);
                    } else {
                        resolve(result);
                    }
                } else {
                    resolve(null);
                }
            };
            request.onerror = () => reject(request.error);
        });
    }

    async set(url, blob) {
        await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const data = {
                url,
                blob,
                timestamp: Date.now()
            };
            const request = store.put(data);

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    async delete(url) {
        await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.delete(url);

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    async clearExpired() {
        await this.init();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const index = store.index('timestamp');
            const request = index.openCursor();
            const now = Date.now();

            request.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    if (now - cursor.value.timestamp > CACHE_DURATION) {
                        cursor.delete();
                    }
                    cursor.continue();
                } else {
                    resolve();
                }
            };
            request.onerror = () => reject(request.error);
        });
    }
}

// Create singleton instance
const cacheDB = new ImageCacheDB();

// Start fetching a bit before the image scrolls into view.
const VIEWPORT_ROOT_MARGIN = '200px';

// How many object URLs we keep alive once nothing displays them any more.
// Bounded so long browsing sessions cannot leak an unbounded number of blobs.
const MAX_IDLE_OBJECT_URLS = 60;

// In-memory cache for faster access during the same session.
// src -> { objectUrl, refs }
const memoryCache = new Map();

// srcs whose object URL is still alive but unused, in least-recently-released
// order (a Map preserves insertion order, so it doubles as an LRU queue).
const idleUrls = new Map();

const revokeObjectUrl = (objectUrl) => {
    if (typeof URL !== 'undefined' && URL.revokeObjectURL) {
        URL.revokeObjectURL(objectUrl);
    }
};

const evictIdleUrls = () => {
    while (idleUrls.size > MAX_IDLE_OBJECT_URLS) {
        const oldest = idleUrls.keys().next().value;
        idleUrls.delete(oldest);
        const entry = memoryCache.get(oldest);
        if (entry) {
            memoryCache.delete(oldest);
            revokeObjectUrl(entry.objectUrl);
        }
    }
};

/**
 * Register one more consumer of the object URL cached for `src`.
 * Creates the entry from `blob` when the src is not cached yet.
 */
const acquireObjectUrl = (src, blob) => {
    let entry = memoryCache.get(src);
    if (!entry) {
        entry = { objectUrl: URL.createObjectURL(blob), refs: 0 };
        memoryCache.set(src, entry);
    }
    entry.refs += 1;
    idleUrls.delete(src);
    return entry.objectUrl;
};

/**
 * Drop one consumer. The URL survives (idle) until it is evicted by the LRU,
 * so remounting the same image stays free.
 */
const releaseObjectUrl = (src) => {
    const entry = memoryCache.get(src);
    if (!entry) return;

    entry.refs = Math.max(0, entry.refs - 1);
    if (entry.refs === 0) {
        idleUrls.set(src, true);
        evictIdleUrls();
    }
};

/**
 * Forget a src whose blob is gone from IndexedDB. Entries still displayed by a
 * component are left alone so we never revoke a URL that is on screen.
 */
const evictObjectUrl = (src) => {
    const entry = memoryCache.get(src);
    if (!entry || entry.refs > 0) return;

    memoryCache.delete(src);
    idleUrls.delete(src);
    revokeObjectUrl(entry.objectUrl);
};

const revokeAllObjectUrls = () => {
    memoryCache.forEach((entry) => revokeObjectUrl(entry.objectUrl));
    memoryCache.clear();
    idleUrls.clear();
};

/**
 * Read a blob from the persistent cache. Storage errors (private mode, quota,
 * no IndexedDB at all) resolve to null so the caller just refetches.
 */
const readCachedBlob = async (src) => {
    try {
        const cached = await cacheDB.get(src);
        return cached ? cached.blob : null;
    } catch (err) {
        return null;
    }
};

const writeCachedBlob = async (src, blob) => {
    try {
        await cacheDB.set(src, blob);
    } catch (err) {
        // Displaying the image matters more than persisting it.
    }
};

/**
 * Custom hook for caching images.
 *
 * The image is only fetched once its container is near the viewport, so a long
 * grid no longer downloads every full-size picture on mount. Object URLs are
 * reference counted and revoked when nothing displays them any more.
 *
 * @param {string} src - Image URL to cache
 * @param {boolean} enabled - Whether caching is enabled (default: true)
 * @returns {Object} - { containerRef, cachedSrc, loading, error }
 */
function useImageCache(src, enabled = true) {
    const containerRef = useRef(null);
    const [cachedSrc, setCachedSrc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const shouldLoad = useNearViewport(containerRef, {
        rootMargin: VIEWPORT_ROOT_MARGIN,
        enabled: Boolean(src) && enabled,
    });

    useEffect(() => {
        if (!src || !enabled) {
            setCachedSrc(src);
            setLoading(false);
            setError(null);
            return undefined;
        }

        if (!shouldLoad) {
            setCachedSrc(null);
            setLoading(true);
            setError(null);
            return undefined;
        }

        let isMounted = true;
        let acquiredSrc = null;

        const show = (objectUrl) => {
            // Unmounted while the blob was in flight: hand the reference we
            // just took straight back, the cleanup below already ran.
            if (!isMounted) {
                releaseObjectUrl(src);
                return;
            }
            acquiredSrc = src;
            setCachedSrc(objectUrl);
            setLoading(false);
        };

        const loadImage = async () => {
            try {
                setLoading(true);
                setError(null);

                // Check memory cache first
                if (memoryCache.has(src)) {
                    show(acquireObjectUrl(src));
                    return;
                }

                // Check IndexedDB cache
                const cached = await readCachedBlob(src);
                if (cached) {
                    show(acquireObjectUrl(src, cached));
                    return;
                }

                // Fetch from network
                const response = await fetch(src);
                if (!response.ok) {
                    throw new Error(`Failed to fetch image: ${response.statusText}`);
                }

                const blob = await response.blob();

                // Store in IndexedDB (best effort: a full or missing store must
                // not keep the image from being displayed)
                await writeCachedBlob(src, blob);

                show(acquireObjectUrl(src, blob));
            } catch (err) {
                console.error('Image cache error:', err);
                if (isMounted) {
                    setError(err);
                    // Fallback to original src
                    setCachedSrc(src);
                    setLoading(false);
                }
            }
        };

        loadImage();

        return () => {
            isMounted = false;
            if (acquiredSrc) {
                releaseObjectUrl(acquiredSrc);
            }
        };
    }, [src, enabled, shouldLoad]);

    return { containerRef, cachedSrc, loading, error };
}

/**
 * Clear expired cache entries
 * Call this on app startup or periodically
 */
export const clearExpiredCache = async () => {
    try {
        await cacheDB.clearExpired();
    } catch (err) {
        console.error('Failed to clear expired cache:', err);
    }
};

/**
 * Clear all cached images
 */
export const clearAllCache = async () => {
    try {
        revokeAllObjectUrls();
        const db = await cacheDB.init();
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        await new Promise((resolve, reject) => {
            const request = store.clear();
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    } catch (err) {
        console.error('Failed to clear cache:', err);
    }
};

/**
 * Get cache statistics
 */
export const getCacheStats = async () => {
    try {
        const db = await cacheDB.init();
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);

        return new Promise((resolve, reject) => {
            const countRequest = store.count();
            countRequest.onsuccess = () => {
                const count = countRequest.result;
                resolve({
                    totalItems: count,
                    memoryItems: memoryCache.size
                });
            };
            countRequest.onerror = () => reject(countRequest.error);
        });
    } catch (err) {
        console.error('Failed to get cache stats:', err);
        return { totalItems: 0, memoryItems: 0 };
    }
};

export default useImageCache;
